import { Controller } from "@hotwired/stimulus"
import { getSavedTheme } from "../theme"
import { needsThemeScenerySync, sceneryForTheme, syncSceneryToSavedTheme } from "../zen_theme_scenery"

const STAR_TINTS = ["#ffffff", "#ffffff", "#ffffff", "#dbe7ff", "#ffe9d0"]
const STAR_SIZES = [1, 1, 1, 2, 2, 3]
const SCENE_NAMES = ["dawn", "day", "dusk", "night"]
// Zen landscape types and their scenery cycles. "mountain" is the shared
// default scene (also used on home/marketing); "future" and "koi" are
// zen-only alternate landscapes layered inside the same bg element.
const ZEN_LANDSCAPES = {
    mountain: ["dawn", "day", "dusk", "night", "ocean", "lavender", "forest"],
    future: ["dawn", "day", "dusk", "night", "coast", "garden", "orbit"],
    koi: ["dawn", "day", "dusk", "night", "blossom", "autumn", "moss"]
}
const ZEN_VARIATION_CLASSES = Object.values(ZEN_LANDSCAPES).flat().filter((id) => !SCENE_NAMES.includes(id)).map((id) => `zen-scenery-${id}`)
// After this long without pointer/scroll activity the scene freezes in place
// (reusing the hidden-tab --paused plumbing) to save battery on parked tabs.
// Zen is exempt: it's a deliberately ambient screen users watch without input.
const IDLE_STILL_MS = 5 * 60 * 1000

// mulberry32: tiny seeded PRNG so the star field is stable across
// navigations within a session (seed persisted in sessionStorage).
function seededRandom(seed) {
    let a = seed >>> 0
    return function () {
        a = (a + 0x6d2b79f5) | 0
        let t = Math.imul(a ^ (a >>> 15), 1 | a)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

// Animates the hero sky scene: picks a time-of-day palette (crossfaded on
// changes), builds the star field client-side, drives depth-aware parallax
// from pointer/tilt/idle-drift through one lerped rAF loop, and fires
// occasional shooting stars. Everything pauses while the tab is hidden and
// attaches/detaches live when prefers-reduced-motion changes.
export default class extends Controller {
    static targets = ["layer", "glow", "meteor", "scroll", "bg", "rays"]
    // still: the member chose a sky with nothing moving (UserSetting#home_background
    // "still"). Handled exactly like prefers-reduced-motion, which already freezes
    // every layer, so the two can never drift apart.
    static values = { still: Boolean }

    connect() {
        this._motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
        this._reducedMotion = this._motionQuery.matches || this.stillValue
        this._zen = this.element.classList.contains("assistant-phone--zen")
        this._coarsePointer = window.matchMedia("(pointer: coarse)").matches

        this._tx = 0
        this._ty = 0
        this._cx = 0
        this._cy = 0
        this._tiltX = 0
        this._tiltY = 0
        this._tiltActive = false
        this._tiltListening = false
        this._tiltGestureBound = false
        this._tiltPermission = null
        this._idleBlend = 0
        this._lastPointerAt = performance.now()
        this._lastActivityAt = performance.now()
        this._idleTimer = null
        this._idleStilled = false
        this._running = false
        this._raf = null
        this._scrollRaf = null
        this._meteorTimer = null
        this._burstTimers = []
        this._skyFadeTimer = null
        this._sceneTimer = null
        this._scene = null
        this._manualScenery = null
        this._motionAttached = false
        this._hidden = document.hidden

        this._onMove = this._onMove.bind(this)
        this._onLeave = this._onLeave.bind(this)
        this._onScroll = this._onScroll.bind(this)
        this._onVisibility = this._onVisibility.bind(this)
        this._onMotionChange = this._onMotionChange.bind(this)
        this._onTilt = this._onTilt.bind(this)
        this._onTiltGesture = this._onTiltGesture.bind(this)
        this._tick = this._tick.bind(this)

        this._buildStars()
        this._jitterClouds()
        // Outside Zen (which has its own zen-scenery controller), the hero
        // itself reacts to theme toggles so dark mode flips the sky to night.
        if (!this._zen) {
            this._onScenerySync = (event) => {
                const { sceneryId, landscape, animate } = event.detail
                this.applyZenScenery(sceneryId, animate, landscape || "mountain")
            }
            document.addEventListener("emailpa:scenerysync", this._onScenerySync)
            if (needsThemeScenerySync(getSavedTheme())) syncSceneryToSavedTheme({ animate: false })
        }
        if (this._zen || this.element.classList.contains("assistant-phone--home")) {
            const savedLandscape = localStorage.getItem("zen-landscape-id")
            const landscape = ZEN_LANDSCAPES[savedLandscape] ? savedLandscape : "mountain"
            const sceneryKey = landscape === "mountain" ? "zen-scenery-id" : `zen-scenery-id-${landscape}`
            const saved = localStorage.getItem(sceneryKey)
            if (saved && ZEN_LANDSCAPES[landscape].includes(saved)) {
                this.applyZenScenery(saved, false, landscape)
            } else if (landscape !== "mountain") {
                this.applyZenScenery(this._defaultScene(), false, landscape)
            } else {
                this._setScene(false)
            }
            this._sceneTimer = this._zen ? null : setInterval(() => {
                if (!this._manualScenery) this._setScene(true)
            }, 60000)
        } else {
            this._setScene(false)
            this._sceneTimer = setInterval(() => this._setScene(true), 60000)
        }

        document.addEventListener("visibilitychange", this._onVisibility)
        this._motionQuery.addEventListener("change", this._onMotionChange)
        this._applyMotionPreference()
    }

    disconnect() {
        if (this._onScenerySync) document.removeEventListener("emailpa:scenerysync", this._onScenerySync)
        document.removeEventListener("visibilitychange", this._onVisibility)
        this._motionQuery.removeEventListener("change", this._onMotionChange)
        if (this._sceneTimer) clearInterval(this._sceneTimer)
        if (this._skyFadeTimer) clearTimeout(this._skyFadeTimer)
        this._detachMotion()
    }

    _bg() {
        return this.element.querySelector(".assistant-home-bg")
    }

    // --- Motion preference (live prefers-reduced-motion switching) ---

    _onMotionChange() {
        this._applyMotionPreference()
    }

    _applyMotionPreference() {
        this._reducedMotion = this._motionQuery.matches || this.stillValue
        const bg = this._bg()
        if (bg) bg.classList.toggle("assistant-home-bg--reduced", this._reducedMotion)
        if (this._reducedMotion) {
            this._detachMotion()
        } else {
            this._attachMotion()
        }
    }

    _attachMotion() {
        if (this._motionAttached) return
        this._motionAttached = true
        this._lastPointerAt = performance.now()
        this.element.addEventListener("pointermove", this._onMove)
        this.element.addEventListener("pointerleave", this._onLeave)
        if (this.hasScrollTarget && this.hasBgTarget) {
            this.scrollTarget.addEventListener("scroll", this._onScroll, { passive: true })
            this._onScroll()
        }
        if (!this._hidden) {
            this._scheduleMeteor()
            this._attachTilt()
            if (this._zen) this._start()
        }
        this._scheduleIdleStill()
    }

    _detachMotion() {
        this._motionAttached = false
        this.element.removeEventListener("pointermove", this._onMove)
        this.element.removeEventListener("pointerleave", this._onLeave)
        if (this.hasScrollTarget) this.scrollTarget.removeEventListener("scroll", this._onScroll)
        this._detachTilt()
        this._stopTimers()
        this._clearIdleTimer()
        this._resetMotionStyles()
    }

    _stopTimers() {
        if (this._raf) cancelAnimationFrame(this._raf)
        this._raf = null
        this._running = false
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf)
        this._scrollRaf = null
        if (this._meteorTimer) clearTimeout(this._meteorTimer)
        this._meteorTimer = null
        this._burstTimers.forEach((timer) => clearTimeout(timer))
        this._burstTimers = []
        this.element.querySelectorAll(".home-shooting-star--extra").forEach((el) => el.remove())
    }

    _resetMotionStyles() {
        this._tx = 0
        this._ty = 0
        this._cx = 0
        this._cy = 0
        this._idleBlend = 0
        this.layerTargets.forEach((layer) => { layer.style.transform = "" })
        if (this.hasGlowTarget) this.glowTarget.style.transform = ""
        if (this.hasRaysTarget) {
            this.raysTarget.style.removeProperty("--rays-shift-x")
            this.raysTarget.style.removeProperty("--rays-shift-y")
        }
        this.element.style.removeProperty("--hero-scroll")
    }

    // --- Tab visibility: freeze CSS animations, timers, rAF and tilt ---

    _onVisibility() {
        this._hidden = document.hidden
        if (!this._hidden) {
            this._idleStilled = false
            this._lastActivityAt = performance.now()
        }
        const bg = this._bg()
        if (bg) bg.classList.toggle("assistant-home-bg--paused", this._hidden)
        if (this._hidden) {
            this._stopTimers()
            this._clearIdleTimer()
            this._detachTilt()
        } else if (this._motionAttached) {
            this._scheduleMeteor()
            this._attachTilt()
            this._start()
            this._scheduleIdleStill()
        }
    }

    // --- Long-idle stilling (non-Zen): freeze the scene after 5 quiet minutes ---

    _clearIdleTimer() {
        if (this._idleTimer) clearTimeout(this._idleTimer)
        this._idleTimer = null
    }

    _scheduleIdleStill() {
        this._clearIdleTimer()
        if (this._zen || this._hidden || this._reducedMotion || !this._motionAttached) return
        const remaining = Math.max(1000, IDLE_STILL_MS - (performance.now() - this._lastActivityAt))
        this._idleTimer = setTimeout(() => {
            this._idleTimer = null
            if (performance.now() - this._lastActivityAt >= IDLE_STILL_MS - 500) {
                this._idleStilled = true
                const bg = this._bg()
                if (bg) bg.classList.add("assistant-home-bg--paused")
                this._stopTimers()
            } else {
                this._scheduleIdleStill()
            }
        }, remaining)
    }

    // Pointer/scroll activity: wake a stilled scene and push back the timer.
    // The pending timeout self-extends on fire, so this stays cheap per event.
    _noteActivity() {
        this._lastActivityAt = performance.now()
        if (!this._idleStilled) return
        this._idleStilled = false
        if (!this._hidden) {
            const bg = this._bg()
            if (bg) bg.classList.remove("assistant-home-bg--paused")
            if (this._motionAttached) {
                this._scheduleMeteor()
                this._scheduleIdleStill()
            }
        }
    }

    // --- Client-side star field (seeded, stable per session) ---

    _starSeed() {
        try {
            let seed = window.sessionStorage.getItem("assistantHeroStarSeed")
            if (!seed) {
                seed = String(Math.floor(Math.random() * 4294967296))
                window.sessionStorage.setItem("assistantHeroStarSeed", seed)
            }
            return parseInt(seed, 10)
        } catch (_error) {
            return Math.floor(Math.random() * 4294967296)
        }
    }

    _buildStars() {
        const container = this.element.querySelector(".home-stars")
        if (!container) return
        container.querySelectorAll(".home-star").forEach((star) => star.remove())
        const rand = seededRandom(this._starSeed())
        const frag = document.createDocumentFragment()
        for (let i = 0; i < 44; i++) {
            const star = document.createElement("span")
            star.className = "home-star"
            const size = STAR_SIZES[Math.floor(rand() * STAR_SIZES.length)]
            const tint = STAR_TINTS[Math.floor(rand() * STAR_TINTS.length)]
            star.style.cssText = `top: ${Math.floor(rand() * 59)}%; left: ${Math.floor(rand() * 101)}%; width: ${size}px; height: ${size}px; background: ${tint}; animation-delay: ${(rand() * 6).toFixed(2)}s; animation-duration: ${(2.6 + rand() * 3.8).toFixed(2)}s`
            frag.appendChild(star)
        }
        for (let i = 0; i < 3; i++) {
            const star = document.createElement("span")
            star.className = "home-star home-star--bright"
            star.style.cssText = `top: ${4 + Math.floor(rand() * 27)}%; left: ${10 + Math.floor(rand() * 81)}%; animation-delay: ${(rand() * 9).toFixed(2)}s`
            frag.appendChild(star)
        }
        container.prepend(frag)
    }

    // Per-cloud hue/brightness/scale jitter so the shared cloud sprite doesn't
    // read as copy-pasted. Relative multipliers only, so scene palettes and
    // time-of-day swaps still apply underneath.
    _jitterClouds() {
        const rand = seededRandom(this._starSeed() ^ 0x9e3779b9)
        this.element.querySelectorAll(".home-cloud").forEach((cloud) => {
            cloud.style.setProperty("--cloud-jitter", (0.88 + rand() * 0.3).toFixed(3))
            cloud.style.setProperty("--cloud-hue", `${((rand() - 0.5) * 24).toFixed(1)}deg`)
            cloud.style.setProperty("--cloud-bright", (0.94 + rand() * 0.14).toFixed(3))
        })
    }

    // --- Time-of-day scene (crossfaded, refreshed every minute) ---

    // Dawn is the CSS default; day/dusk/night override its palette vars.
    // Uses the visitor's local clock so the scene matches what's outside.
    applyZenScenery(sceneryId, animate, landscapeId = "mountain") {
        const sceneries = ZEN_LANDSCAPES[landscapeId]
        if (!sceneries || !sceneries.includes(sceneryId)) return

        const bg = this._bg()
        if (!bg) return

        this._manualScenery = sceneryId

        if (animate && this._scene && !this._reducedMotion && !this._hidden) this._crossfadeSky(bg)
        this._scene = sceneryId

        bg.classList.remove("assistant-home-bg--day", "assistant-home-bg--dusk", "assistant-home-bg--night")
        bg.classList.remove("zen-landscape-future", "zen-landscape-koi")
        ZEN_VARIATION_CLASSES.forEach((cls) => bg.classList.remove(cls))

        if (landscapeId !== "mountain") bg.classList.add(`zen-landscape-${landscapeId}`)

        if (SCENE_NAMES.includes(sceneryId)) {
            if (sceneryId !== "dawn") bg.classList.add(`assistant-home-bg--${sceneryId}`)
        } else {
            bg.classList.add(`zen-scenery-${sceneryId}`)
        }

        const phone = this.element.closest(".assistant-phone")
        if (phone) {
            SCENE_NAMES.forEach((name) => phone.classList.remove(`assistant-home-scene-${name}`))
            const sceneClass = SCENE_NAMES.includes(sceneryId) ? sceneryId : "dusk"
            phone.classList.add(`assistant-home-scene-${sceneClass}`)
        }

        document.body.classList.toggle("assistant-home-scene-day", sceneryId === "day")
    }

    _timeScene() {
        const hour = new Date().getHours()
        if (hour >= 10 && hour < 17) return "day"
        if (hour >= 17 && hour < 21) return "dusk"
        if (hour >= 21 || hour < 5) return "night"
        return "dawn"
    }

    // Dark app theme pins the sky to the theme's scene (night) regardless of
    // the clock; light theme follows the visitor's local time of day.
    _defaultScene() {
        if (getSavedTheme() === "dark") return sceneryForTheme("dark")
        return this._timeScene()
    }

    _setScene(animate) {
        if (this._manualScenery) return

        const bg = this._bg()
        if (!bg) return
        const scene = this._defaultScene()
        if (scene === this._scene) return

        if (animate && this._scene && !this._reducedMotion && !this._hidden) this._crossfadeSky(bg)
        this._scene = scene

        bg.classList.remove("assistant-home-bg--day", "assistant-home-bg--dusk", "assistant-home-bg--night")
        if (scene !== "dawn") bg.classList.add(`assistant-home-bg--${scene}`)

        const phone = this.element.closest(".assistant-phone")
        if (phone) {
            SCENE_NAMES.forEach((name) => phone.classList.remove(`assistant-home-scene-${name}`))
            phone.classList.add(`assistant-home-scene-${scene}`)
        }

        document.body.classList.toggle("assistant-home-scene-day", scene === "day")
    }

    // The sky gradient itself can't interpolate, so freeze the outgoing sky in
    // an overlay div and fade it out over the incoming one. The color/number
    // scene vars crossfade via registered @property transitions in CSS.
    _crossfadeSky(bg) {
        const sky = bg.querySelector(".home-sky")
        if (!sky) return
        bg.querySelectorAll(".home-sky-fade").forEach((el) => el.remove())
        const computed = getComputedStyle(sky)
        const fade = document.createElement("div")
        fade.className = "home-sky-fade"
        fade.style.backgroundImage = computed.backgroundImage
        fade.style.backgroundSize = computed.backgroundSize
        fade.style.backgroundPosition = computed.backgroundPosition
        sky.insertAdjacentElement("afterend", fade)
        void fade.offsetWidth
        fade.classList.add("is-fading")
        if (this._skyFadeTimer) clearTimeout(this._skyFadeTimer)
        this._skyFadeTimer = setTimeout(() => fade.remove(), 2400)
    }

    // --- Scroll-linked scrim + header glass ramp (home only) ---

    // Fixed full-height scene: --hero-scroll (0..1) deepens the bottom scrim
    // and ramps the header glass in CSS. It used to fade the scene itself too;
    // that washed it out against the white phone (see .assistant-home-bg).
    _onScroll() {
        this._noteActivity()
        if (this._scrollRaf) return
        this._scrollRaf = requestAnimationFrame(() => {
            this._scrollRaf = null
            const top = this.scrollTarget.scrollTop
            const progress = Math.min(1, Math.max(0, top / 280))
            this.element.style.setProperty("--hero-scroll", progress.toFixed(3))
        })
    }

    // --- Pointer / tilt / idle-drift parallax rig ---

    _onMove(e) {
        const rect = this.element.getBoundingClientRect()
        this._tx = (e.clientX - rect.left) / rect.width - 0.5
        this._ty = (e.clientY - rect.top) / rect.height - 0.5
        this._lastPointerAt = performance.now()
        this._noteActivity()
        this._start()
    }

    _onLeave() {
        this._tx = 0
        this._ty = 0
        this._start()
    }

    // Device tilt (Zen on touch devices). iOS requires a permission prompt
    // that can only be triggered from a user gesture; anywhere it's not
    // available or granted we silently skip.
    _attachTilt() {
        if (!this._zen || !this._coarsePointer || this._reducedMotion || this._tiltListening) return
        if (typeof window.DeviceOrientationEvent === "undefined") return
        if (typeof DeviceOrientationEvent.requestPermission === "function") {
            if (this._tiltPermission === "granted") {
                window.addEventListener("deviceorientation", this._onTilt)
                this._tiltListening = true
            } else if (this._tiltPermission === null && !this._tiltGestureBound) {
                this._tiltGestureBound = true
                this.element.addEventListener("click", this._onTiltGesture, { once: true })
            }
        } else {
            window.addEventListener("deviceorientation", this._onTilt)
            this._tiltListening = true
        }
    }

    _detachTilt() {
        window.removeEventListener("deviceorientation", this._onTilt)
        this._tiltListening = false
        this.element.removeEventListener("click", this._onTiltGesture)
        this._tiltGestureBound = false
        this._tiltActive = false
    }

    _onTiltGesture() {
        this._tiltGestureBound = false
        DeviceOrientationEvent.requestPermission().then((state) => {
            this._tiltPermission = state
            if (state === "granted") this._attachTilt()
        }).catch(() => {
            this._tiltPermission = "denied"
        })
    }

    _onTilt(e) {
        if (e.gamma === null || e.beta === null) return
        this._tiltActive = true
        this._tiltX = Math.max(-0.5, Math.min(0.5, e.gamma / 60))
        this._tiltY = Math.max(-0.5, Math.min(0.5, (e.beta - 40) / 60))
        this._start()
    }

    _start() {
        if (this._running || this._hidden || this._reducedMotion) return
        this._running = true
        this._raf = requestAnimationFrame(this._tick)
    }

    _tick() {
        // Blend pointer, device tilt (30%) and, on Zen after 3s of stillness,
        // a slow sine drift into the target, then ease toward it for weighty,
        // damped camera-rig motion.
        let tx = this._tx
        let ty = this._ty
        if (this._tiltActive) {
            tx = tx * 0.7 + this._tiltX * 0.3
            ty = ty * 0.7 + this._tiltY * 0.3
        }
        if (this._zen) {
            const idle = performance.now() - this._lastPointerAt > 3000
            this._idleBlend += ((idle ? 1 : 0) - this._idleBlend) * 0.01
            if (this._idleBlend > 0.001) {
                const t = performance.now() / 1000
                tx += Math.sin((t * Math.PI * 2) / 20) * 0.03 * this._idleBlend
                ty += Math.cos((t * Math.PI * 2) / 26) * 0.03 * this._idleBlend
            }
        }

        this._cx += (tx - this._cx) * 0.08
        this._cy += (ty - this._cy) * 0.08

        // Zen keeps the loop alive for ambient drift; elsewhere it settles out
        const settled = !this._zen && Math.abs(tx - this._cx) < 0.0005 && Math.abs(ty - this._cy) < 0.0005
        if (settled) {
            this._cx = tx
            this._cy = ty
        }

        this._apply()

        if (settled || this._hidden) {
            this._running = false
            this._raf = null
        } else {
            this._raf = requestAnimationFrame(this._tick)
        }
    }

    _apply() {
        this.layerTargets.forEach((layer) => {
            const depth = parseFloat(layer.dataset.depth || "1")
            const scale = parseFloat(layer.dataset.scale || "1.08")
            const x = this._cx * -14 * depth
            const y = this._cy * -10 * depth
            layer.style.transform = `scale(${scale}) translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`
        })
        if (this.hasRaysTarget) {
            // Rays animate transform in CSS, so parallax rides in via vars
            const depth = parseFloat(this.raysTarget.dataset.depth || "0.05")
            this.raysTarget.style.setProperty("--rays-shift-x", `${(this._cx * -14 * depth).toFixed(2)}px`)
            this.raysTarget.style.setProperty("--rays-shift-y", `${(this._cy * -10 * depth).toFixed(2)}px`)
        }
        if (this.hasGlowTarget) {
            // Preserve the CSS translateX(-50%) centering while adding parallax
            const gx = this._cx * 18
            const gy = this._cy * 12
            this.glowTarget.style.transform = `translate(calc(-50% + ${gx.toFixed(2)}px), ${gy.toFixed(2)}px)`
        }
    }

    // --- Shooting stars ---

    _scheduleMeteor() {
        if (!this.hasMeteorTarget || this._meteorTimer || this._hidden) return
        const delay = 7000 + Math.random() * 14000
        this._meteorTimer = setTimeout(() => {
            this._meteorTimer = null
            this._fireMeteor(this.meteorTarget)
            if (Math.random() < 0.3) this._fireBurst()
            this._scheduleMeteor()
        }, delay)
    }

    _fireMeteor(meteor) {
        meteor.style.top = `${5 + Math.random() * 26}%`
        meteor.style.left = `${5 + Math.random() * 45}%`
        meteor.style.setProperty("--meteor-angle", `${(18 + Math.random() * 12).toFixed(1)}deg`)
        meteor.classList.remove("is-firing")
        // Force a reflow so re-adding the class restarts the CSS animation
        void meteor.offsetWidth
        meteor.classList.add("is-firing")
    }

    _fireBurst() {
        const extras = 1 + Math.round(Math.random())
        for (let i = 1; i <= extras; i++) {
            const timer = setTimeout(() => {
                const clone = this.meteorTarget.cloneNode(false)
                clone.removeAttribute("data-assistant-hero-target")
                clone.classList.add("home-shooting-star--extra")
                clone.classList.remove("is-firing")
                this.meteorTarget.parentNode.appendChild(clone)
                this._fireMeteor(clone)
                this._burstTimers.push(setTimeout(() => clone.remove(), 1400))
            }, i * 200)
            this._burstTimers.push(timer)
        }
    }
}
